import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';

export type BatchQuickActionKind = 'approve' | 'decline' | 'interim';

type ExpansionMap = Map<string, BatchQuickActionKind>;

type BatchExpansionContextValue = {
    expanded: ExpansionMap;
    expandedRecordIds: string[];
    toggle: (rowId: string, kind: BatchQuickActionKind) => void;
    close: (rowId: string) => void;
    closeAll: () => void;
    getKindFor: (rowId: string) => BatchQuickActionKind | null;
};

const BatchExpansionContext = createContext<BatchExpansionContextValue | null>(
    null,
);

type ProviderProps = Readonly<React.PropsWithChildren>;

export function BatchExpansionProvider({ children }: ProviderProps) {
    const [expanded, setExpanded] = useState<ExpansionMap>(() => new Map());

    const toggle = useCallback((rowId: string, kind: BatchQuickActionKind) => {
        setExpanded(previous => {
            if (previous.get(rowId) === kind) {
                return new Map();
            }
            return new Map([[rowId, kind]]);
        });
    }, []);

    const closeAll = useCallback(() => {
        setExpanded(new Map());
    }, []);

    const close = useCallback((rowId: string) => {
        setExpanded(previous => {
            if (!previous.has(rowId)) {
                return previous;
            }
            const next = new Map(previous);
            next.delete(rowId);
            return next;
        });
    }, []);

    const getKindFor = useCallback(
        (rowId: string) => expanded.get(rowId) ?? null,
        [expanded],
    );

    const expandedRecordIds = useMemo(
        () => Array.from(expanded.keys()),
        [expanded],
    );

    const value = useMemo<BatchExpansionContextValue>(
        () => ({
            expanded,
            expandedRecordIds,
            toggle,
            close,
            closeAll,
            getKindFor,
        }),
        [expanded, expandedRecordIds, toggle, close, closeAll, getKindFor],
    );

    return (
        <BatchExpansionContext.Provider value={value}>
            {children}
        </BatchExpansionContext.Provider>
    );
}

export function useBatchExpansion(): BatchExpansionContextValue {
    const context = useContext(BatchExpansionContext);
    if (!context) {
        throw new Error(
            'useBatchExpansion must be used within a BatchExpansionProvider',
        );
    }
    return context;
}

export function useOptionalBatchExpansion(): BatchExpansionContextValue | null {
    return useContext(BatchExpansionContext);
}
